/**
 * One row of the dataset form's settings table: what is being configured on
 * the left, the control that configures it on the right.
 *
 * The form had grown into a single narrow column where every field carried a
 * two-line explanation underneath. That reads as a wall: the explanations
 * take more vertical space than the controls, so scanning "what can I set
 * here?" means reading everything. A two-column table answers that question
 * at a glance and moves the prose to a hover tooltip, where it is available
 * to whoever wants it and invisible to whoever does not.
 *
 * The tooltip is `title` on the label — plus `aria-describedby` on the
 * control, via {@link hintId}. A description a sighted user can reach by
 * hovering has to reach a screen-reader user too, and `title` alone is not
 * dependable for that.
 *
 * Labels are therefore **terse on purpose** — a noun phrase naming the
 * setting, never the sentence explaining it. The sentence is the `hint`. A
 * label that carries its own explanation defeats the whole arrangement: the
 * left column becomes prose again and there is nothing left for the tooltip
 * to add.
 */

import { useId, type ReactNode } from 'react';

export function SettingRow({
  htmlFor,
  label,
  hint,
  children,
}: {
  /**
   * Id of the control this row labels.
   *
   * Omit it when the row holds **several** controls (the min/max pair). One
   * `<label for>` can only point at one of them, so naming the group that way
   * would leave the others unlabelled while looking correct; the row then
   * renders a `role="group"` named by the same text instead.
   */
  htmlFor?: string;
  /** Terse: the setting's name, not its explanation. See the file header. */
  label: string;
  /** Shown on hover and exposed as the control's description. */
  hint?: string;
  /**
   * The control, given the id of the hint element to describe itself with.
   *
   * Errors stay with the control rather than moving up here: `Input` and
   * `SearchableSelect` already render their own message *and* the matching
   * red border, and a row that rendered it too would show it twice.
   */
  children: (hintId: string | undefined) => ReactNode;
}) {
  const generatedHintId = useId();
  const labelId = useId();
  const hintId = hint ? generatedHintId : undefined;

  const labelClass = 'pt-2 text-sm font-medium text-text-primary';

  return (
    <div
      className="
        grid items-start gap-x-6 gap-y-1 border-b border-border-default py-3
        last:border-b-0
        sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)]
      "
    >
      {htmlFor ? (
        <label htmlFor={htmlFor} title={hint} className={labelClass}>
          {label}
        </label>
      ) : (
        <span id={labelId} title={hint} className={labelClass}>
          {label}
        </span>
      )}
      <div
        className="min-w-0"
        {...(htmlFor ? {} : { role: 'group', 'aria-labelledby': labelId, 'aria-describedby': hintId })}
      >
        {children(hintId)}
        {hint && (
          // Visually hidden: the same text the tooltip shows, so the control's
          // `aria-describedby` has something real to point at.
          <span id={hintId} className="sr-only">
            {hint}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * One control inside a grouped row, with its own short caption.
 *
 * Only for rows that hold several controls — a lone control is named by the
 * row's own `<label>`, and a second visible name there would be noise. The
 * caption is a real `<label for>`, so the pair reads as
 * "Unknown work, min" rather than as two anonymous boxes.
 */
export function SettingSubField({
  htmlFor,
  caption,
  children,
}: {
  htmlFor: string;
  caption: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <label htmlFor={htmlFor} className="shrink-0 text-xs text-text-muted">
        {caption}
      </label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** The table the rows sit in — a plain container, kept here so the spacing
 * lives in one place rather than at every call site. */
export function SettingsTable({ children }: { children: ReactNode }) {
  return <div className="rounded-sm border border-border-default px-4">{children}</div>;
}

/**
 * A named group of settings, with the table already inside it.
 *
 * The sections carry the domain model: a dataset describes a chain, feature
 * <- deliverable <- work item, and each level's section owns the query that
 * selects it *and* the field pointing one level up. Grouping them is what
 * makes "which field links a deliverable to its feature?" answerable by
 * looking, instead of by reading a hint on a field sitting six rows from the
 * query it belongs to.
 */
export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  const headingId = useId();

  return (
    // `aria-labelledby` makes this a NAMED region: it is what lets a screen
    // reader announce "Deliverables" when entering the group, and what lets a
    // test assert that a field lives in the section it belongs to rather than
    // merely somewhere on the page.
    <section aria-labelledby={headingId} className="space-y-2">
      <div>
        <h3 id={headingId} className="text-sm font-semibold text-text-primary">
          {title}
        </h3>
        {description && <p className="text-xs text-text-muted">{description}</p>}
      </div>
      <SettingsTable>{children}</SettingsTable>
    </section>
  );
}
